import React, { createContext, useContext, useEffect, useState } from "react";
import { featureFlags, FeatureFlagKey } from "../featureFlags.ts";

type FeatureFlags = Record<string, boolean>;

interface FeatureFlagContextType {
  flags: FeatureFlags;
  setFlag: (key: FeatureFlagKey, value: boolean) => void;
  isEnabled: (key: FeatureFlagKey) => boolean;
  refreshFlags: () => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const stored = localStorage.getItem("featureFlags");
    return stored ? JSON.parse(stored) : featureFlags;
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem("featureFlags", JSON.stringify(flags));
  }, [flags]);

  const setFlag = (key: FeatureFlagKey, value: boolean) => {
    setFlags((prev) => ({ ...prev, [key]: value }));
  };

  const isEnabled = (key: FeatureFlagKey) => Boolean(flags[key]);

  const refreshFlags = () => {
    const stored = localStorage.getItem("featureFlags");
    if (stored) {
      setFlags(JSON.parse(stored));
    } else {
      setFlags(featureFlags);
    }
  };

  return (
    <FeatureFlagContext.Provider
      value={{ flags, setFlag, isEnabled, refreshFlags }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }
  return context;
};
